/**
 * =============================================================================
 * TOAST COMPONENT
 * =============================================================================
 *
 * A notification toast component for displaying success, error, and warning
 * messages to admin users. Integrates with BigDesign components for consistent
 * styling within the BigCommerce admin interface.
 *
 * PHASE 1 TASK: P1-7 - Add Toast Notifications for Admin Actions
 *
 * Features:
 * - Success, error, and warning variants
 * - Auto-dismiss with configurable timeout
 * - Manual dismiss via close button
 * - Accessible with proper ARIA attributes
 * - Animated entrance and exit
 *
 * =============================================================================
 */

import React, { useEffect, useCallback, useState } from 'react';
import { Box, Flex, FlexItem, Text, Button } from '@bigcommerce/big-design';
import {
  CheckCircleIcon,
  ErrorIcon,
  WarningIcon,
  CloseIcon,
} from '@bigcommerce/big-design-icons';
import { tokens } from '../../tokens';

/**
 * Toast variant types.
 */
export type ToastVariant = 'success' | 'error' | 'warning';

/**
 * Toast data structure.
 */
export interface ToastData {
  /** Unique identifier for the toast */
  id: string;
  /** The message to display */
  message: string;
  /** The visual variant (determines color and icon) */
  variant: ToastVariant;
  /** Auto-dismiss duration in milliseconds (0 = no auto-dismiss) */
  duration?: number;
  /**
   * B-6 §I: bumped when a same-(variant, message) toast is coalesced into
   * this one. A dep of the auto-dismiss timer effect (restarts the countdown
   * on the SAME DOM node — no remount, no animation replay) and the
   * provider's eviction-recency ordinal (higher = shown/refreshed later).
   */
  refreshNonce?: number;
}

/**
 * One announcement event for the SR-only live region (B-6 §I). One is enqueued
 * per `addToast` call — including coalesced duplicates, which change no visible
 * toast DOM but must still be announced (today every duplicate mounts a fresh
 * `role="alert"` node and is announced; coalescing without this would silently
 * swallow repeated failures).
 *
 * These flow to `ToastContainer` as a FIFO **queue** drained one at a time
 * (B-6 impl-review CL-1). A single scalar slot could not honor the "announce
 * every event" contract: two `addToast` calls in one React batch (e.g. the
 * `useRfi` create flow — message + status-transition toast) commit only the
 * final announcement state, so the live-region effect runs once and swallows
 * the first event. The queue gives each event its own empty→message cycle.
 */
export interface ToastAnnouncement {
  /** Monotonic id — FIFO ordering key and the value the container acks. */
  id: number;
  /** The toast message to announce. */
  text: string;
}

/**
 * Props for individual Toast component.
 */
interface ToastProps {
  /** Toast data */
  toast: ToastData;
  /** Callback when toast should be dismissed */
  onDismiss: (id: string) => void;
  /** Optional test ID for testing */
  'data-testid'?: string;
}

/**
 * Default auto-dismiss duration in milliseconds.
 */
const DEFAULT_DURATION = 5000;

/**
 * B-6 §I: a persistent live region must transition through an empty state
 * before inserting text again, or NVDA does not announce a repeated message
 * (the AdminShell B-4 human-gate finding — same constant/value there).
 */
const LIVE_REGION_REPEAT_DELAY_MS = 100;

/**
 * How long an announcement stays in the live region after insertion.
 * Assistive technology announces the insertion mutation itself; the text
 * has no residual purpose and is cleared so the SR-only node never outlives
 * the visible toast it mirrors.
 */
const LIVE_REGION_RESIDENCE_MS = 1000;

/**
 * Get the icon component for a toast variant.
 */
function getToastIcon(variant: ToastVariant): React.ReactElement {
  switch (variant) {
    case 'success':
      return <CheckCircleIcon color="success" />;
    case 'error':
      return <ErrorIcon color="danger" />;
    case 'warning':
      return <WarningIcon color="warning" />;
    default:
      return <CheckCircleIcon color="success" />;
  }
}

/**
 * Get the background color for a toast variant.
 */
function getToastBackgroundColor(variant: ToastVariant): string {
  switch (variant) {
    case 'success':
      return tokens.color.toast.success.surface;
    case 'error':
      return tokens.color.toast.error.surface;
    case 'warning':
      return tokens.color.toast.warning.surface;
    default:
      return tokens.color.toast.success.surface;
  }
}

/**
 * Get the border color for a toast variant.
 */
function getToastBorderColor(variant: ToastVariant): string {
  switch (variant) {
    case 'success':
      return tokens.color.toast.success.accent;
    case 'error':
      return tokens.color.toast.error.accent;
    case 'warning':
      return tokens.color.toast.warning.accent;
    default:
      return tokens.color.toast.success.accent;
  }
}

/**
 * Individual Toast component.
 *
 * Displays a single toast notification with icon, message, and dismiss button.
 * Auto-dismisses after the specified duration.
 */
export function Toast({
  toast,
  onDismiss,
  'data-testid': testId,
}: ToastProps): React.ReactElement {
  const { id, message, variant, duration = DEFAULT_DURATION, refreshNonce } = toast;

  // Auto-dismiss effect. refreshNonce is a dependency so a coalesced
  // duplicate (B-6 §I de-dupe) RESTARTS the countdown with the incoming
  // duration — same DOM node, no remount; the reference below keeps the
  // dependency visibly load-bearing for the exhaustive-deps rule.
  useEffect(() => {
    void refreshNonce;
    if (duration <= 0) return;

    const timerId = setTimeout(() => {
      onDismiss(id);
    }, duration);

    return () => {
      clearTimeout(timerId);
    };
  }, [id, duration, onDismiss, refreshNonce]);

  const handleDismiss = useCallback(() => {
    onDismiss(id);
  }, [id, onDismiss]);

  return (
    <Box
      role="alert"
      aria-live="polite"
      data-testid={testId ?? `toast-${id}`}
      data-toast-slide
      style={{
        backgroundColor: getToastBackgroundColor(variant),
        borderLeft: `4px solid ${getToastBorderColor(variant)}`,
        borderRadius: tokens.dimension.radius.sm,
        padding: `${tokens.dimension.space.md} ${tokens.dimension.space.lg}`,
        marginBottom: tokens.dimension.space.sm,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        animation: 'toastSlideIn 0.3s ease-out',
        minWidth: '300px',
        maxWidth: '500px',
      }}
    >
      <Flex alignItems="center" justifyContent="space-between">
        <FlexItem>
          <Flex alignItems="center" flexGap="0.75rem">
            <FlexItem>{getToastIcon(variant)}</FlexItem>
            <FlexItem>
              <Text
                data-testid={testId ? `${testId}-message` : `toast-${id}-message`}
              >
                {message}
              </Text>
            </FlexItem>
          </Flex>
        </FlexItem>
        <FlexItem>
          <Button
            variant="subtle"
            onClick={handleDismiss}
            iconOnly={<CloseIcon size="medium" />}
            aria-label="Dismiss notification"
            data-testid={testId ? `${testId}-dismiss` : `toast-${id}-dismiss`}
          />
        </FlexItem>
      </Flex>
    </Box>
  );
}

/**
 * Props for ToastContainer component.
 */
interface ToastContainerProps {
  /** Array of toasts to display */
  toasts: ToastData[];
  /** Callback when a toast should be dismissed */
  onDismiss: (id: string) => void;
  /**
   * B-6 §I announcement channel — a FIFO queue of pending events. When this
   * prop is PRESENT (even as an empty array) the container keeps a persistent
   * SR-only live region mounted and drains the queue one event at a time, each
   * through the proven empty → message transition; it acks the drained head via
   * `onAnnounced` so the provider can prune it. When absent, the container
   * renders exactly as before B-6 (nothing while `toasts` is empty) — the
   * provider always passes it; direct consumers opt in.
   */
  announcements?: ToastAnnouncement[];
  /** Ack the head announcement once it has been announced (queue advance). */
  onAnnounced?: (id: number) => void;
  /** Optional test ID for testing */
  'data-testid'?: string;
}

/**
 * Toast container component.
 *
 * Renders all active toasts in a fixed position container.
 * Toasts stack vertically from the top-right corner.
 */
export function ToastContainer({
  toasts,
  onDismiss,
  announcements,
  onAnnounced,
  'data-testid': testId,
}: ToastContainerProps): React.ReactElement | null {
  const [liveMessage, setLiveMessage] = useState('');

  // B-6 §I announcement parity (CL-1: serialized FIFO drain). Process the head
  // of the queue: clear the region, insert its text after a beat so assistive
  // technology observes a real empty → message transition (AdminShell
  // precedent), then clear again and ACK the head so the provider prunes it and
  // the next event's cycle begins. Keying the effect on the head id (+ text)
  // means a later event appended mid-cycle does NOT interrupt the current
  // announcement — it waits its turn. This is what lets batched/burst events
  // (which collapse a single scalar slot) each get announced.
  const head = announcements && announcements.length > 0 ? announcements[0] : null;
  const headId = head?.id ?? null;
  const headText = head?.text ?? null;
  useEffect(() => {
    setLiveMessage('');
    if (headId == null) return undefined;
    const insertTimer = window.setTimeout(() => {
      setLiveMessage(headText ?? '');
    }, LIVE_REGION_REPEAT_DELAY_MS);
    const doneTimer = window.setTimeout(() => {
      setLiveMessage('');
      onAnnounced?.(headId);
    }, LIVE_REGION_REPEAT_DELAY_MS + LIVE_REGION_RESIDENCE_MS);
    return () => {
      window.clearTimeout(insertTimer);
      window.clearTimeout(doneTimer);
    };
  }, [headId, headText, onAnnounced]);

  // Persistent (announcement-prop consumers only) — a live region must exist
  // BEFORE content lands in it or the insertion is not announced.
  const liveRegion =
    announcements === undefined ? null : (
      <div
        data-testid="toast-live-region"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
        }}
      >
        {liveMessage && <span data-testid="toast-live-message">{liveMessage}</span>}
      </div>
    );

  if (toasts.length === 0) {
    return liveRegion;
  }

  return (
    <>
      {liveRegion}
      <style>
        {`
          @keyframes toastSlideIn {
            from {
              opacity: 0;
              transform: translateX(100%);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          @media (prefers-reduced-motion: reduce) {
            [data-toast-slide] { animation: none !important; }
          }
        `}
      </style>
      <Box
        data-testid={testId ?? 'toast-container'}
        style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
        }}
      >
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            toast={toast}
            onDismiss={onDismiss}
            data-testid={testId ? `${testId}-toast-${toast.id}` : undefined}
          />
        ))}
      </Box>
    </>
  );
}
